import { browser } from "$app/environment";

import type { DatadogRum } from "@datadog/browser-rum";

import { env } from "$env/dynamic/public";
import {
  PUBLIC_DD_RUM_APPLICATION_ID,
  PUBLIC_DD_RUM_CLIENT_TOKEN,
} from "$env/static/public";
import { isIgnoredConsoleLog } from "$lib/core/console-log-filter";
import {
  redactSecretValues,
  redactUrlSecrets,
  redactUrlsInText,
} from "$lib/core/pii-scrub";
import { isBrowserExtensionCspReport } from "$lib/core/rum-event-filter";
import { isSupersededRequestAbort } from "$lib/core/swr";
import type { User } from "$lib/identity/user";

// Page-scoped state mirroring the RUM SDK's browser singleton. The module
// also evaluates during SSR (via Analytics.svelte), so every mutator guards
// on `browser` — module state must never be shared across server requests.
let rum: DatadogRum | undefined;
let cloudfrontRequestId: string | null | undefined;
let currentUser: User | undefined;
// Held here until RUM has initialized, then applied to every later event.
const globalContext = new Map<string, unknown>();

// RUM event variants carry URLs in different shapes; scrub what is present.
type UrlBearing = {
  view?: { url?: string };
  resource?: { url?: string };
  error?: { message?: string; resource?: { url?: string } };
};

/** Mirroring ddtrace's query-string obfuscation for backend traces. */
function scrubEventUrls(event: UrlBearing): void {
  if (event.view?.url) event.view.url = redactUrlSecrets(event.view.url);
  if (event.resource?.url) {
    event.resource.url = redactUrlSecrets(event.resource.url);
  }
  if (event.error?.resource?.url) {
    event.error.resource.url = redactUrlSecrets(event.error.resource.url);
  }
  if (event.error?.message) {
    event.error.message = redactUrlsInText(event.error.message);
  }
}

/**
 * Initialize Datadog RUM and Logs.
 */
export async function initDatadogObservability(): Promise<void> {
  if (!browser) return;
  if (!PUBLIC_DD_RUM_APPLICATION_ID || !PUBLIC_DD_RUM_CLIENT_TOKEN) return;

  const [{ datadogRum }, { datadogLogs }] = await Promise.all([
    import("@datadog/browser-rum"),
    import("@datadog/browser-logs"),
  ]);

  const shared = {
    clientToken: PUBLIC_DD_RUM_CLIENT_TOKEN,
    site: "datadoghq.com",
    service: "modal-web",
    env: import.meta.env.MODE,
    // Deploy-time git SHA, re-exposed by the pod env as PUBLIC_DD_VERSION
    // (see modal-frontend-deployment.yml); unset locally, so no version tag.
    version: env.PUBLIC_DD_VERSION,
    // Same-origin intake (src/routes/(analytics-proxies)/dd) so ad blockers don't drop sessions.
    proxy: "/dd",
  } as const;

  datadogRum.init({
    ...shared,
    applicationId: PUBLIC_DD_RUM_APPLICATION_ID,
    // Ingest every session; storage sampling happens server-side via the
    // RUM retention filters in ops/pulumi/datadog (all error sessions, 20%
    // of authenticated sessions, 1% of the rest).
    sessionSampleRate: 100,
    // Session Replay is intentionally off.
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Inject trace context into same-origin API requests so frontend
    // sessions can join backend traces end to end.
    traceSampleRate: 100,
    allowedTracingUrls: [
      {
        match: window.location.origin,
        propagatorTypes: ["tracecontext", "datadog"],
      },
    ],
    beforeSend: (event, context) => {
      if (isBrowserExtensionCspReport(event)) return false;
      if (
        event.type === "error" &&
        isSupersededRequestAbort((context as { error?: unknown }).error)
      ) {
        return false;
      }
      scrubEventUrls(event);
      return true;
    },
  });
  rum = datadogRum;
  // Usage Attribution tag: bills RUM sessions to the owning team.
  datadogRum.setGlobalContextProperty("team", "product-experience");
  globalContext.forEach((value, key) =>
    datadogRum.setGlobalContextProperty(key, value),
  );
  if (cloudfrontRequestId) {
    datadogRum.setGlobalContextProperty(
      "cloudfront_request_id",
      cloudfrontRequestId,
    );
  }
  applyUser(currentUser);

  datadogLogs.init({
    ...shared,
    forwardConsoleLogs: ["log", "info", "warn", "error"],
    beforeSend: (log) => {
      if (isIgnoredConsoleLog(log.message)) return false;
      log.message = redactSecretValues(log.message);
      return true;
    },
  });
}

/**
 * Attach the CloudFront request ID of the document load to the RUM session.
 * First non-null value wins: revalidations of the root layout re-supply the
 * ID of the data request, not the document load.
 */
export function attachCloudfrontRequestId(id: string | null | undefined): void {
  if (!browser || cloudfrontRequestId) return;
  cloudfrontRequestId = id;
  if (rum && id) {
    rum.setGlobalContextProperty("cloudfront_request_id", id);
  }
}

function applyUser(user: User | undefined): void {
  if (!rum) return;
  if (user?.loggedIn) {
    rum.setUser({
      id: user.userId,
      name: user.name,
      email: user.notificationEmail,
      username: user.username,
      is_admin: String(user.isAdmin),
      last_used_workspace: user.lastUsedWorkspace,
    });
  } else {
    rum.clearUser();
  }
}

/** Attach the logged-in user's identity to the RUM session, or clear it. */
export function identifyDatadogUser(user: User | undefined): void {
  if (!browser) return;
  currentUser = user;
  applyUser(user);
}

/** Attach a property to every later RUM event; undefined removes it. */
export function setDatadogContext(key: string, value: unknown): void {
  if (!browser) return;
  if (value === undefined) {
    globalContext.delete(key);
    rum?.removeGlobalContextProperty(key);
  } else {
    globalContext.set(key, value);
    rum?.setGlobalContextProperty(key, value);
  }
}

/** Report a handled error to RUM, with context describing where it happened. */
export function reportDatadogError(
  error: unknown,
  context: Record<string, unknown>,
): void {
  if (!browser) return;
  rum?.addError(error, context);
}
